/**
 * Spec 051 — error-screen parity, Phase 1 (observe).
 *
 * A single shared canonical appearance record for the caregiver-facing error
 * screens that spec 049 never touched (404, 403, 422, 500). It REUSES the
 * diagnostic payload spec 049 established (`UnicornShownProperties`, built by
 * `createUnicornShownProperties`) rather than inventing a parallel one, and adds
 * exactly the two dimensions the spec mandates:
 *   - `screen`         (FR-002) — identifies WHICH screen appeared, so screens
 *                                  cannot be conflated in analysis.
 *   - `arrival_reason` (FR-004) — a machine-readable reason handed over by the
 *                                  system that delivered the caregiver, captured
 *                                  for diagnosis, never displayed and never the
 *                                  free-text description (FR-011).
 *
 * This additive extension of 049's payload is recorded as an amendment on the
 * governing spec, per its Assumption ("if it needs extending, that is an
 * amendment here, not a parallel payload").
 */
import {
  UnicornConnectivityState,
  UnicornRecoveryOutcome,
  UnicornShownProperties,
  UNICORN_INCIDENT_CODE_PATTERN,
  createUnicornShownProperties,
  normalizeUnicornIncidentCode,
  normalizeUnicornRoute,
} from './unicorn';

export const ERROR_SCREENS = [
  'page-not-found',
  'forbidden',
  'unprocessable',
  'server-error',
  // Reserved for the spec-049 "unicorn". It keeps emitting its own
  // `unicorn_shown` record unchanged (FR-003); the value exists so analysis has
  // a stable identifier for the fifth screen.
  'global-error',
] as const;

export type ErrorScreen = typeof ERROR_SCREENS[number];

export function isErrorScreen(value: unknown): value is ErrorScreen {
  return ERROR_SCREENS.some(screen => screen === value);
}

/** The one canonical event name shared by the four newly-instrumented screens. */
export const ERROR_SCREEN_EVENT_NAME = 'error_screen_shown';

/**
 * Navigation/history state key under which a delivering system hands the vetted
 * machine-readable arrival reason to the screen (FR-004). A short code only —
 * never the provider's free-text description.
 */
export const ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY = 'errorScreenArrivalReason';

/**
 * Navigation/history state key under which a delivering system may hand a
 * spec-049 `cause` to the screen (e.g. the auth callback tagging an expired
 * session as `auth`). Absent handoffs record as `unknown`.
 */
export const ERROR_SCREEN_CAUSE_STATE_KEY = 'errorScreenCause';

/** Recorded when no reason was handed over on arrival. */
export const ERROR_SCREEN_NO_REASON = 'none';

/**
 * Recorded when a reason WAS handed over but failed the safety check — so the
 * fact that a reason arrived is preserved without capturing an unvetted value.
 */
export const ERROR_SCREEN_INVALID_REASON = 'invalid';

/**
 * FR-011 safety gate: only a short machine token may be captured from an
 * arrival. Auth0 error codes (`access_denied`, `login_required`, `server_error`,
 * …) match; a free-text `error_description`, a raw URL or a route does not, and
 * is dropped to {@link ERROR_SCREEN_INVALID_REASON}.
 */
export const ERROR_SCREEN_ARRIVAL_REASON_PATTERN = /^[a-z][a-z0-9_-]{0,63}$/;

export interface ErrorScreenShownProperties extends UnicornShownProperties {
  screen: ErrorScreen;
  arrival_reason: string;
}

/**
 * Vets a value carried on an arrival before it may be recorded (FR-004/FR-011).
 * Anything that is not a short machine token becomes `invalid`; absence becomes
 * `none`.
 */
export function normalizeArrivalReason(value: unknown): string {
  if (value === undefined || value === null || value === '') {
    return ERROR_SCREEN_NO_REASON;
  }
  if (
    typeof value === 'string' &&
    ERROR_SCREEN_ARRIVAL_REASON_PATTERN.test(value)
  ) {
    return value;
  }
  return ERROR_SCREEN_INVALID_REASON;
}

/**
 * Builds the shared appearance record. `staleness` and `service_worker` are
 * fixed at `unresolved`: those are spec-049's stale-build recovery probes, which
 * are recovery machinery out of scope for this observe-only increment (FR-003).
 */
export function createErrorScreenShownProperties(
  screen: ErrorScreen,
  cause: unknown,
  producer: unknown,
  buildIdentity: unknown,
  connectivity: UnicornConnectivityState | unknown,
  incidentCode: unknown,
  arrivalReason: unknown,
): ErrorScreenShownProperties {
  return {
    ...createUnicornShownProperties(
      cause,
      producer,
      buildIdentity,
      'unresolved',
      'unresolved',
      connectivity,
      incidentCode,
    ),
    screen,
    arrival_reason: normalizeArrivalReason(arrivalReason),
  };
}

/* -------------------------------------------------------------------------- *
 * Spec 051 — error-screen parity, Phase 2 (recover).
 *
 * FR-005/FR-006/FR-007 give every screen a state-changing recovery action;
 * FR-012 makes whether that action SUCCEEDED observable. This reuses spec 049's
 * proven deferred-outcome mechanism (a synchronous "attempt" event proves only
 * a click, not success): the screen writes a pending record before it navigates,
 * and a single `NavigationEnd` hook resolves the record on the landing and emits
 * one canonical outcome event. It reuses 049's outcome vocabulary
 * (`UnicornRecoveryOutcome`), route normaliser and incident-code primitives
 * rather than forking a parallel telemetry path, and keeps its OWN sessionStorage
 * key so the two outcome paths can never cross-resolve.
 * -------------------------------------------------------------------------- */

/** The one canonical outcome event name, parallel to `error_screen_shown`. */
export const ERROR_SCREEN_RECOVERY_OUTCOME_EVENT_NAME =
  'error_screen_recovery_outcome';

/**
 * The recovery actions an error screen may offer. Each can CHANGE the failing
 * state (FR-005), never merely re-navigate into it:
 *  - `go-home`            — an in-app navigation to the caregiver's start.
 *  - `interactive-reauth` — a full interactive re-login (FR-007, the recovery
 *                            for an expired/invalid session).
 *  - `retry-reload`       — a fresh full-page load that re-attempts the failing
 *                            operation once a transient backend outage clears
 *                            (FR-006, so the 5xx screen is never home-only).
 */
export const ERROR_SCREEN_RECOVERY_ACTIONS = [
  'go-home',
  'interactive-reauth',
  'retry-reload',
] as const;
export type ErrorScreenRecoveryAction =
  typeof ERROR_SCREEN_RECOVERY_ACTIONS[number];

export function isErrorScreenRecoveryAction(
  value: unknown,
): value is ErrorScreenRecoveryAction {
  return ERROR_SCREEN_RECOVERY_ACTIONS.some(action => action === value);
}

/** sessionStorage key for the pending recovery record — DISTINCT from 049's. */
export const ERROR_SCREEN_RECOVERY_PENDING_KEY =
  'rettx.errorscreen.recovery.pending';
/** sessionStorage key prefix for the burst-safe one-shot guard. */
export const ERROR_SCREEN_RECOVERY_GUARD_PREFIX =
  'rettx.errorscreen.recovery.guard';
/** Loop guard: bounds caregiver dead time between attempts on one action. */
export const ERROR_SCREEN_RECOVERY_GUARD_TTL_MS = 5 * 60 * 1000;
/** After this horizon a pending recovery is truthfully `expired`, not failed. */
export const ERROR_SCREEN_RECOVERY_PENDING_TTL_MS = 10 * 60 * 1000;

/**
 * The record written to sessionStorage the instant a recovery action is taken,
 * carrying only vetted values (FR-011): no raw URLs, routes or free text.
 */
export interface ErrorScreenPendingRecovery {
  incidentCode: string;
  screen: ErrorScreen;
  action: ErrorScreenRecoveryAction;
  arrivalReason: string;
  startedAt: number;
  shownAt: number;
  destination: string;
}

export interface ErrorScreenRecoveryOutcomeProperties
  extends Record<string, string | number> {
  screen: ErrorScreen;
  action: ErrorScreenRecoveryAction;
  outcome: UnicornRecoveryOutcome;
  incident_code: string;
  arrival_reason: string;
  destination: string;
  duration_ms: number;
}

export function getErrorScreenRecoveryGuardKey(
  incidentCode: string,
  action: ErrorScreenRecoveryAction,
): string {
  return `${ERROR_SCREEN_RECOVERY_GUARD_PREFIX}.${normalizeUnicornIncidentCode(
    incidentCode,
  )}.${action}`;
}

export function createErrorScreenRecoveryOutcomeProperties(
  pending: ErrorScreenPendingRecovery,
  outcome: UnicornRecoveryOutcome,
  now: number,
): ErrorScreenRecoveryOutcomeProperties {
  return {
    screen: pending.screen,
    action: pending.action,
    outcome,
    incident_code: normalizeUnicornIncidentCode(pending.incidentCode),
    arrival_reason: normalizeArrivalReason(pending.arrivalReason),
    destination: normalizeUnicornRoute(pending.destination),
    duration_ms: normalizeRecoveryDuration(now - pending.shownAt),
  };
}

/**
 * Parses and validates a candidate pending record. Anything malformed returns
 * `undefined` so a corrupt or forged value can never drive an emission.
 */
export function parseErrorScreenPendingRecovery(
  value: unknown,
): ErrorScreenPendingRecovery | undefined {
  let candidate: unknown = value;
  if (typeof value === 'string') {
    try {
      candidate = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  if (typeof candidate !== 'object' || candidate === null) {
    return undefined;
  }
  const record = candidate as Record<string, unknown>;
  if (
    !UNICORN_INCIDENT_CODE_PATTERN.test(String(record['incidentCode'] ?? '')) ||
    !isErrorScreen(record['screen']) ||
    !isErrorScreenRecoveryAction(record['action']) ||
    typeof record['arrivalReason'] !== 'string' ||
    !isSafeRecoveryTimestamp(record['startedAt']) ||
    !isSafeRecoveryTimestamp(record['shownAt']) ||
    typeof record['destination'] !== 'string'
  ) {
    return undefined;
  }
  return {
    incidentCode: String(record['incidentCode']),
    screen: record['screen'],
    action: record['action'],
    arrivalReason: normalizeArrivalReason(record['arrivalReason']),
    startedAt: record['startedAt'],
    shownAt: record['shownAt'],
    destination: normalizeUnicornRoute(record['destination']),
  };
}

function isSafeRecoveryTimestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function normalizeRecoveryDuration(value: number): number {
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}
